import { Link } from "react-router-dom";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { NeonButton } from "@/components/ui/NeonButton";
import { listFaqs } from "@/integrations/backend/faqs";
import { useEffect, useState } from "react";

export default function HowItWorks() {
  const [faqs, setFaqs] = useState<{id:string; question:string; answer:string}[]>([]);
  useEffect(() => {
    (async () => {
      try {
        const rows = await listFaqs();
        // Sort ascending by created_at to match original behavior
        setFaqs([...rows].sort((a, b) => (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0)));
      } catch {
        setFaqs([]);
      }
    })();
  }, []);
  const images = {
    hero: "https://images.unsplash.com/photo-1434596922112-19c563067271?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0",
    onboarding: "https://images.unsplash.com/photo-1567598508481-65985588e295?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    program: "https://images.unsplash.com/photo-1738523686619-1b3b5d2405ee?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    guidance: "https://images.unsplash.com/photo-1645005512942-ebb840398a6a?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    progress: "https://images.unsplash.com/photo-1580983693000-5654250f64d1?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0",
    optimization: "https://images.unsplash.com/photo-1623804421482-721b3901c125?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  };

  return (
    <>
      <MarketingHeader />
      <main className="min-h-screen gradient-dark pt-24">
        {/* Cinematic Hero similar to index */}
        <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
          {/* Background image with overlay */}
          <div className="absolute inset-0">
            <img src={images.hero} alt="How it works background" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-dark-bg/95 via-dark-bg/80 to-dark-bg"></div>
          </div>

          {/* Centered stripes */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 w-px h-full bg-gradient-to-b from-transparent via-primary/25 to-transparent" style={{ left: 'calc(50% - 220px)' }} />
            <div className="absolute top-0 w-px h-full bg-gradient-to-b from-transparent via-primary/20 to-transparent" style={{ left: 'calc(50% + 220px)', animationDelay: '0.5s' }} />
          </div>

          <div className="container mx-auto px-6 text-center relative z-10">
            <h1 className="text-5xl md:text-6xl font-black text-white leading-tight">
              How It <span className="text-primary">Works</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-light mt-4 max-w-3xl mx-auto">
              Smarter training, fewer decisions. AI + expert coaching guide every rep.
            </p>
            <Link to="/signup">
              <NeonButton className="px-8 py-4 text-base mt-8">Get Started</NeonButton>
            </Link>
          </div>
        </section>

        {/* Steps */}
        <section className="py-16">
          <div className="container mx-auto px-6 grid md:grid-cols-2 gap-10 max-w-6xl">
            {[
              {
                n: "01",
                title: "Sign up and complete onboarding",
                text:
                  "Tell us about your goals, current fitness level, and equipment. Your plan starts smart.",
                img: images.onboarding,
              },
              {
                n: "02",
                title: "Meet your trainer",
                text:
                  "Have a 1:1 call with your certified trainer. Your program fits your life, not the other way around.",
                img: "https://images.unsplash.com/photo-1605296867724-fa87a8ef53fd?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0",
              },
              {
                n: "03",
                title: "Get your personalized program",
                text:
                  "Your trainer and our AI collaborate to create a tailored plan and weekly schedule.",
                img: images.program,
              },
              {
                n: "04",
                title: "Train with guidance, not guesswork",
                text:
                  "Real-time guidance and form tips walk you through each workout so you can focus on execution.",
                img: images.guidance,
              },
              {
                n: "05",
                title: "Track your progress",
                text:
                  "Log sets, reps, and energy levels effortlessly. We surface trends and insights automatically.",
                img: images.progress,
              },
              {
                n: "06",
                title: "Continuous optimization",
                text:
                  "Frequent assessments let AI and coaches fine‑tune your plan for steady, long‑term progress.",
                img: images.optimization,
              },
            ].map((step) => (
              <div key={step.n} className="group rounded-2xl overflow-hidden border border-primary/20 bg-dark-card">
                <div className="relative h-48">
                  <div className="absolute inset-0 overflow-hidden">
                    <img
                      src={step.img}
                      alt={step.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-dark-card via-dark-card/50 to-transparent" />
                  <div className="absolute top-4 left-4 w-14 h-14 bg-primary rounded-xl flex items-center justify-center text-2xl font-black text-black shadow-neon-glow">
                    {step.n}
                  </div>
                </div>
                <div className="p-6 space-y-2">
                  <h3 className="text-2xl font-bold text-white">{step.title}</h3>
                  <p className="text-gray-light">{step.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ (moved after steps) */}
        <section className="py-16 border-t border-primary/10">
          <div className="container mx-auto px-6 max-w-5xl">
            <h2 className="text-4xl md:text-5xl font-black text-white text-center mb-10">FAQs</h2>
            <div className="space-y-4">
              {faqs.map((f)=> (
                <div key={f.id} className="bg-dark-card/80 border border-primary/15 rounded-2xl p-6">
                  <h3 className="text-xl font-bold text-primary mb-2">{f.question}</h3>
                  <p className="text-gray-light">{f.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="container mx-auto px-6 text-center max-w-3xl">
            <h2 className="text-4xl md:text-5xl font-black text-white">
              Ready to <span className="text-primary">start</span>?
            </h2>
            <p className="text-gray-light text-xl mt-3">
              Join thousands training smarter with adaptive coaching.
            </p>
            <div className="mt-6 flex justify-center">
              <Link to="/signup">
                <NeonButton className="px-8 py-4 text-base">Get Started</NeonButton>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <MarketingFooter />
    </>
  );
}


